// ABOUTME: Two or more short options in one rounded track, the selected one filled in.
// ABOUTME: The compact switch used where a control has to sit inside a panel or grid header.

import { ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import { tokens } from "../../../theme";

/**
 * ShPillToggle
 *
 * A switch between a handful of named options, small enough to sit beside a title or a search
 * box. Picking an option is the whole interaction, so the group never falls back to nothing:
 * clicking the option already selected leaves it selected.
 *
 * - options: [{ value, label }], where label is a translation key. Keep the labels short —
 *   the buttons sit side by side and the track grows with them.
 * - value / onChange: the selected value and what to do with the next one.
 * - title: optional tooltip on the whole group, for a switch whose effect its labels cannot
 *   carry on their own.
 */
const ShPillToggle = ({ options, value, onChange, title }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { t } = useTranslation();

    // The chosen option is filled in the same grey a contained Shbutton uses, so it reads as a
    // pressed button; the others stay on the light track behind it. Grey rather than a colour,
    // so the toggle sits behind whatever it belongs to instead of competing with it.
    const isDark = theme.palette.mode === "dark";
    const selectedBackground = isDark ? colors.txt["secondary"] : colors.grey[400];
    const selectedColor = isDark ? colors.grey[400] : colors.grey[0];

    return (
        <ToggleButtonGroup
            exclusive
            value={value}
            title={title}
            onChange={(event, next) => next !== null && onChange(next)}
            sx={{
                padding: 0.5,
                borderRadius: 5,
                background: isDark ? colors.grey[300] : colors.grey[100],
                height: 32,
                gap: 0.5,
            }}
        >
            {options.map((option) => (
                <ToggleButton
                    key={String(option.value)}
                    value={option.value}
                    sx={{
                        border: 'none',
                        borderRadius: "16px !important",
                        px: 1.5,
                        height: 24,
                        textTransform: 'none',
                        fontSize: 13,
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        color: colors.txt['secondary'],
                        '&.Mui-selected': {
                            background: selectedBackground,
                            color: selectedColor,
                            fontWeight: 700,
                            '&:hover': { background: selectedBackground },
                        },
                    }}
                >
                    {t(option.label)}
                </ToggleButton>
            ))}
        </ToggleButtonGroup>
    );
};

export default ShPillToggle;
